/**
 * Tag Input - edits a list of tags as removable chips.
 *
 * Enter or comma commits the typed tag, Backspace on an empty input removes the last one,
 * and pasting "a, b, c" adds all three. Duplicates (case-insensitive) are ignored.
 */

import React, { useState } from 'react';
import { CloseIcon } from './icons';
import './TagInput.css';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  id?: string;
}

export const TagInput: React.FC<TagInputProps> = ({ value, onChange, placeholder, suggestions = [], id }) => {
  const [draft, setDraft] = useState('');

  const addTags = (raw: string) => {
    const existing = new Set(value.map(t => t.toLowerCase()));
    const added: string[] = [];
    for (const part of raw.split(',')) {
      const tag = part.trim();
      if (tag && !existing.has(tag.toLowerCase())) {
        existing.add(tag.toLowerCase());
        added.push(tag);
      }
    }
    if (added.length) onChange([...value, ...added]);
    setDraft('');
  };

  const remaining = suggestions.filter(s => !value.some(t => t.toLowerCase() === s.toLowerCase()));
  const listId = id ? `${id}-suggestions` : undefined;

  return (
    <div className="tag-input" onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}>
      {value.map(tag => (
        <span key={tag} className="tag-input__chip">
          {tag}
          <button
            type="button"
            className="tag-input__remove"
            onClick={(e) => {
              e.stopPropagation();
              onChange(value.filter(t => t !== tag));
            }}
            aria-label={`Remove tag ${tag}`}
          >
            <CloseIcon size={10} />
          </button>
        </span>
      ))}
      <input
        id={id}
        type="text"
        className="tag-input__field"
        value={draft}
        list={listId}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => {
          const next = e.target.value;
          if (next.includes(',')) addTags(next);
          else setDraft(next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addTags(draft);
          } else if (e.key === 'Backspace' && draft === '' && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => draft.trim() && addTags(draft)}
      />
      {listId && remaining.length > 0 && (
        <datalist id={listId}>
          {remaining.map(s => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </div>
  );
};
